import React from "react";
import { Helmet } from "react-helmet-async";
import { Box, Stack, Typography, Select, TextField, Skeleton, MenuItem as MuiMenuItem } from "@mui/material";

import SaveIcon from "@mui/icons-material/Save";
import PreviewIcon from "@mui/icons-material/PreviewOutlined";
import ResetIcon from "@mui/icons-material/RestartAlt";

import useGeneral from "../../../hooks/super/useGeneral";
import useAuth from "../../../hooks/useAuth";

import PreviewModal from "./component/M_Preview";
import { EmailType, UserType } from "../../../utils/constants";
import { Alert, CollapseAlert } from "../../../components/pages/common/styled";
import PageHeader from "../../../components/pages/_new/super/common/PageHeader";
import PrimaryButton from "../../../components/pages/_new/super/common/PrimaryButton";
import OutlinedButton from "../../../components/pages/_new/super/common/OutlinedButton";
import {
  TOKENS,
  SectionCard,
} from "../../../components/pages/_new/super/design";

function getCategoryName(type) {
  switch (type) {
    case EmailType.WELCOME_EMAIL_VERIFICATION:
      return "Welcome";
    case EmailType.PASSWORD_RESET:
      return "Password Reset";
    case EmailType.PAYMENT_SUCCESS:
      return "Payment Success";
    case EmailType.PAYMENT_FAILURE:
      return "Payment Fail";
    case EmailType.SITE_ADD:
      return "Site Add";
    case EmailType.SITE_REMOVE:
      return "Site Delete";
    case EmailType.DDOS_DETECTED:
      return "DDoS Detected";
    case EmailType.CERTS_EXPIRING_SOON:
      return "Certificates Expiring Soon";
    case EmailType.CERTS_EXPIRED:
      return "Certificates Expired";
    default:
      return;
  }
}

function SAEmailConfiguration() {
  const { category, content, getEmailCategory, getEmailContent, updateEmailContent, errMsg, setErr } = useGeneral();
  const { isAuthenticated, adminUserType } = useAuth();

  const [type, setType] = React.useState();
  const [title, setTitle] = React.useState();
  const [from, setFrom] = React.useState();
  const [body, setBody] = React.useState();
  const [sync, setSync] = React.useState(false);

  const changeTitle = (e) => setTitle(e.target.value);
  const changeFrom = (e) => setFrom(e.target.value);
  const changeBody = (e) => setBody(e.target.value);

  React.useEffect(() => {
    if (isAuthenticated) getEmailCategory();
    return () => setErr(null);
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (category === null || category === undefined) return;
    if (category.length === 0) return;
    setType(category[0]?.type);
  }, [category]);

  React.useEffect(() => {
    setTitle(content?.title);
    setFrom(content?.from);
    setBody(content?.content);
    setSync(!!content?.need_sync);
  }, [content]);

  const selectType = (e) => setType(e.target.value);

  React.useEffect(() => {
    if (type === null || type === undefined) return;
    setTitle(null);
    setFrom(null);
    setBody(null);
    getEmailContent(type);
  }, [type]); // eslint-disable-line react-hooks/exhaustive-deps

  const [errOpen, setErrOpen] = React.useState(false);
  React.useEffect(() => {
    if (errMsg) setErrOpen(true);
  }, [errMsg]);

  const reset = () => {
    setTitle(content?.title);
    setFrom(content?.from);
    setBody(content?.content);
  };
  const save = () => updateEmailContent(type, { title, from, content: body });

  const [open, setOpen] = React.useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: `${TOKENS.radiusXs}px`,
      background: TOKENS.bgSurface,
    },
  };
  const labelSx = { fontSize: 12.5, color: TOKENS.fg2, mb: 0.75, fontWeight: 500 };
  const noBody = body === null || body === undefined;
  const canEdit = [UserType.SUPER_ADMIN, UserType.ADMIN].includes(adminUserType);

  return (
    <React.Fragment>
      <Helmet title="SA General Configuration" />
      <PageHeader
        title="Email Configuration"
        description="Email templates for system notifications and account events"
        actions={
          <>
            <OutlinedButton onClick={reset} disabled={noBody} startIcon={<ResetIcon style={{ width: 18, height: 18 }} />}>
              Reset
            </OutlinedButton>
            <OutlinedButton onClick={handleOpen} disabled={noBody} startIcon={<PreviewIcon style={{ width: 18, height: 18 }} />}>
              Preview
            </OutlinedButton>
            <PrimaryButton onClick={save} disabled={!canEdit || noBody} startIcon={<SaveIcon style={{ width: 18, height: 18 }} />}>
              Save
            </PrimaryButton>
          </>
        }
      />
      <Box sx={{ mb: 3 }}>
        <CollapseAlert errOpen={errOpen} setErrOpen={setErrOpen} setErr={setErr} errMsg={errMsg} />
      </Box>

      <Stack spacing={2}>
        <SectionCard title="Category" description="Choose which email template you'd like to edit">
          <Box sx={{ maxWidth: 360 }}>
            <Typography sx={labelSx}>Email category</Typography>
            <Select
              value={type !== undefined && type}
              onChange={selectType}
              fullWidth
              size="small"
              sx={{
                borderRadius: `${TOKENS.radiusXs}px`,
                "& .MuiOutlinedInput-input": { fontSize: 14 },
              }}
            >
              {category?.map((c, i) => (
                <MuiMenuItem key={i} value={c.type}>
                  {getCategoryName(c.type)}
                </MuiMenuItem>
              ))}
            </Select>
          </Box>
        </SectionCard>

        {sync && (
          <Alert severity="error" variant="outlined">
            <Typography sx={{ fontSize: 13 }}>
              Auth0 template differs from this one. Save this template to sync.
            </Typography>
          </Alert>
        )}

        <SectionCard title="Subject & Sender">
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <Box sx={{ flex: 1 }}>
              <Typography sx={labelSx}>Title</Typography>
              {title === null || title === undefined ? (
                <Skeleton height={40} variant="rectangular" sx={{ borderRadius: `${TOKENS.radiusXs}px` }} />
              ) : (
                <TextField fullWidth size="small" value={title} onChange={changeTitle} sx={inputSx} />
              )}
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={labelSx}>From</Typography>
              {from === null || from === undefined ? (
                <Skeleton height={40} variant="rectangular" sx={{ borderRadius: `${TOKENS.radiusXs}px` }} />
              ) : (
                <TextField fullWidth size="small" value={from} onChange={changeFrom} sx={inputSx} />
              )}
            </Box>
          </Stack>
        </SectionCard>

        <SectionCard title="Body" description="HTML markup; supports interpolation tokens">
          {noBody ? (
            <Stack spacing={1}>
              {Array.from({ length: 10 }).map((_, i) => (
                <Skeleton key={i} height={18} variant="rectangular" sx={{ borderRadius: 1 }} />
              ))}
            </Stack>
          ) : (
            <TextField
              fullWidth
              multiline
              maxRows={18}
              minRows={15}
              value={body || ""}
              onChange={changeBody}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: `${TOKENS.radiusXs}px`,
                  background: TOKENS.bgSurface,
                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                  fontSize: 12.5,
                },
              }}
            />
          )}
        </SectionCard>
      </Stack>
      <PreviewModal open={open} handleClose={handleClose} body={body} />
    </React.Fragment>
  );
}
export default SAEmailConfiguration;
